import { FunctionComponent, useCallback } from 'react'

import styled from 'styled-components'

import Text from '../../../components/Text';
import { styles } from '../../../styles/themes/style';
import { useBillingStore } from '../../../stores/billing/useBillingStore';
import Tag from '../../../components/Tag';
import Button from '../../../components/Button';
import { IBillingPlanV2, IPrice } from '../../../interfaces/models/billingPlan';
import { useBillingHandlers } from '../../../services/actions/billing/useBillingHandlers';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from '../../../interfaces/store/appStore';
import { useExternalHandlers } from '../../../services/actions/navigation/useExternalHandlers';
import { formatCurrency, oldBusinessPrices, splitFormattedPriceTwoDecimals } from '../../../utils';
import { calculateSaveAmount } from './CycleToggle';
import { useAuthStore } from '../../../stores/auth/useAuthStore';
import SvgFragment from '../../../fragments/SvgFragment';
import { getActivePrice } from '../../../utils/pricingUtils';
import TooltipWrapper from '../../../components/TooltipWrapper';
import Row from '../../../components/Row';

interface IBillingPlansStyles {
    $backgroundColor?: boolean,
    $border?: boolean,
    $disabled?: boolean,
    $isEnterprise?: boolean
}

interface IBillingPlansProps extends IBillingPlansStyles {
    currency: string,
    selectedCycle: string,
    setShowCompareFeatures?: (e: any) => void
}

const BillingPlans: FunctionComponent<IBillingPlansProps> = ({ selectedCycle, currency, setShowCompareFeatures }) => {
    const { store: { billingPlans } } = useBillingStore()
    const { store: { subscription, trial }} = useAuthStore()
    const { setSelectedPlanAndPriceHandler } = useBillingHandlers()
    const navigate = useNavigate()
    const { openDemoProduct } = useExternalHandlers()

    const enterpriseFeaturesList = [
        {
            "value": "For large organisations"
        },
        {
          "value": "Everything in Business plan and:",
          "italic": true
        },
        {
          "value": "Add over 100+ users"
        },
        {
          "value": "Dedicated customer success team"
        },
        {
          "value": "Custom onboarding & team training"
        },
        {
          "value": "Product advisory board seat"
        },
        {
          "value": "SSO, SAML & OIDC"
        },
        {
          "value": "Custom integrations"
        }
    ]
    const cantSelectSame = subscription?.isValid && !trial && !(subscription?.cancelDate && subscription?.isValid)

    let userDefinedPrice: IPrice | undefined
    const selectedUserBillingPlan = billingPlans.find((plan: IBillingPlanV2) => {
        userDefinedPrice = plan.prices.find((price: IPrice) => price.priceId === subscription.priceId && price.productId === subscription.productId)
        return !!userDefinedPrice
    })
    //Old prices, feautre compatablility
    const selectedUserOldBillingPlan = oldBusinessPrices().includes(subscription.priceId) 
        ? {name: 'Business'} 
        : undefined;
    const setSelectedPlanAndPrice = useCallback((plan: IBillingPlanV2) => {
        if(cantSelectSame && selectedUserBillingPlan && userDefinedPrice && userDefinedPrice?.currency === currency && userDefinedPrice?.interval === selectedCycle && selectedUserBillingPlan?._id === plan?._id){
            return true
        }
        const changeSeats = cantSelectSame && selectedUserBillingPlan && userDefinedPrice && userDefinedPrice?.currency === currency && userDefinedPrice?.interval !== selectedCycle && selectedUserBillingPlan?._id === plan?._id

        const selectedPrice = setSelectedPlanAndPriceHandler(plan, selectedCycle, currency)
        navigate(AppRoutes.BILLING_PLAN, { state: { changeSeats, preSelectedBillingPlan: plan, preSelectedPrice: selectedPrice }})
        
    }, [cantSelectSame, selectedCycle, currency, userDefinedPrice, selectedUserBillingPlan, navigate, setSelectedPlanAndPriceHandler])

    const billingPlansWithSaveAmount = billingPlans.map((product: any) => {
        // Get active prices for both monthly and yearly
        const monthlyPrice = getActivePrice(product.prices, 'month', currency);
        const annualPrice = getActivePrice(product.prices, 'year', currency);
        
        const save = calculateSaveAmount(monthlyPrice, annualPrice)

        // Filter prices to show only active ones for UI
        const activePrices = product.prices.filter((price: any) => {
            if (price.interval === 'month') {
                return price.active === true || (price.active === undefined && product.prices.filter((p: any) => p.interval === 'month' && p.currency === price.currency).length === 1);
            }
            if (price.interval === 'year') {
                return price.active === true || (price.active === undefined && product.prices.filter((p: any) => p.interval === 'year' && p.currency === price.currency).length === 1);
            }
            return true; // Keep other intervals as-is
        });

        // Add save information to yearly prices
        const updatedPrices = activePrices.map((price: any) => {
            if (price.interval === 'year' && save > 0) {
                return {
                    ...price,
                    save: save === 3000 ? `Save 30%` : `2 months off`
                };
            }
            return price;
        });

        return { 
            ...product,
            prices: updatedPrices
        }
      })

    return (
        <Container>
            <PlansRow>
                {billingPlansWithSaveAmount.map((plan: any, index: number) => {
                    // Since we already filtered for active prices above, we can directly get the price
                    const selectedPrice = plan.prices.find((price: any) => price.interval === selectedCycle && price.currency === currency)
                    
                    // calculate credits for each plan
                    const planCredits: Record<string, number> = {
                      'Starter': 600,
                      'Pro': 1200,
                      'Business': 3600
                    };
                    const creditsRaw = planCredits[plan?.name] || 0;
                    const credits = selectedCycle === 'year' ? creditsRaw : Math.ceil(creditsRaw / 12);

                    if (selectedPrice) {
                        const price = selectedPrice?.interval === 'year' ? selectedPrice?.price / 12 : selectedPrice?.price
                        const { integerPart, decimalPart } = splitFormattedPriceTwoDecimals(price)
                        // const [additionalLicenceIntegerPart, additionalLicencedecimalPart] = formatPrice(selectedPrice?.additionalLicence).toString().split('.')
                        const isCurrentPlan = cantSelectSame && selectedUserBillingPlan && userDefinedPrice && userDefinedPrice?.currency === selectedPrice?.currency && userDefinedPrice?.interval === selectedPrice?.interval && selectedUserBillingPlan?._id === plan?._id
                        const isDisabledBusinessStarter = subscription?.status !== 'canceled' && !trial && (selectedUserBillingPlan?.name === 'Business' || selectedUserOldBillingPlan?.name === 'Business') && plan?.name === 'Starter'
                        return (
                            <div key={index}>
                                <AdvancedColaborationCard $backgroundColor={plan?.name === 'Pro'}>
                                    {plan?.name === 'Pro' ? <Text $bold>Used by top dealmakers</Text> : null}
                                </AdvancedColaborationCard>
                                <PlanCard $disabled ={isDisabledBusinessStarter || isCurrentPlan} $border={plan?.name === 'Pro'}>
                                    <div>
                                        <TitleSection>
                                            <Text $bold $black $heading3>{plan?.name}</Text>
                                            {plan?.isPopular && <Tag $backgroundColor={styles.colors.yellow600} $borderColor={styles.colors.black600} $color={styles.colors.yellow600} SVGtype='popularStar' title={'Most popular'} />}
                                        </TitleSection>
                                        <PriceSection>
                                            <PriceWrapper>
                                                <PriceText> {formatCurrency(selectedPrice?.currency)}{integerPart} </PriceText>
                                                {decimalPart !== '00' && <Text $bold $paragraph2 $black>{`.${decimalPart}`}</Text>}
                                                <FractionalPart>
                                                    {selectedPrice?.save && <Tag $height='18px' $color={styles.colors.green600} title={selectedPrice?.save} />}
                                                    <IntervalText>{'month/user'}</IntervalText>
                                                </FractionalPart>
                                            </PriceWrapper>
                                        </PriceSection>
                                        <Button
                                            $type='blue'
                                            $customDisabled={isCurrentPlan || isDisabledBusinessStarter}
                                            disabled={isCurrentPlan || isDisabledBusinessStarter}
                                            onClickHandler={() => setSelectedPlanAndPrice(plan)}>{isCurrentPlan ? 'Your plan' : 'Buy Now'}
                                        </Button>
                                        <FeatureList>
                                            {/* Title feature - displayed first */}
                                            {plan.featuresList
                                                .filter((planFeature: any) => planFeature.title)
                                                .map((planFeature: any, index: number) => (
                                                    <Feature key={`title-${index}`}>
                                                        <Text $black $bold $paragraph1 $italic={planFeature.italic}>
                                                            {selectedCycle === 'year' && planFeature.valueYearly ? planFeature.valueYearly : planFeature.value}
                                                        </Text>
                                                    </Feature>
                                                ))}
                                            
                                            {/* Other highlighted features in CreditsBenefitsDiv */}
                                            {plan.featuresList.some((feature: any) => feature.highlighted && !feature.title) && (
                                                <CreditsBenefitsDiv>
                                                        {plan.featuresList
                                                            .filter((planFeature: any) => planFeature.highlighted && !planFeature.title)
                                                            .map((planFeature: any, index: number) => (
                                                                <BenefitRow key={index}>
                                                                    <StyledSvg primaryColor><SvgFragment type="checkmark" /></StyledSvg>
                                                                    <TooltipWrapper tooltipText={planFeature.tooltip || ''}>
                                                                        <div>
                                                                            <FeatureTitle 
                                                                                style={{ fontStyle: planFeature.italic ? 'italic' : 'normal' }}
                                                                                dangerouslySetInnerHTML={{ __html: selectedCycle === 'year' && planFeature.valueYearly ? planFeature.valueYearly : planFeature.value }}
                                                                            />
                                                                        </div>
                                                                    </TooltipWrapper>
                                                                    {planFeature.comingSoon && (
                                                                        <Badge>
                                                                            <span>Coming soon</span>
                                                                        </Badge>
                                                                    )}
                                                                    {planFeature.isNew && (
                                                                        <Badge>
                                                                            <span>NEW</span>
                                                                        </Badge>
                                                                    )}
                                                                </BenefitRow>
                                                            ))}
                                                </CreditsBenefitsDiv>
                                            )}
                                            
                                            {/* Non-highlighted features */}
                                            {plan.featuresList
                                                .filter((planFeature: any) => !planFeature.highlighted && !planFeature.title)
                                                .map((planFeature: any, index: number) => (
                                                    <Feature key={index} style={{ paddingLeft: '8px' }}>
                                                        <StyledSvg primaryColor><SvgFragment type="checkmark" /></StyledSvg>
                                                        <TooltipWrapper tooltipText={planFeature.tooltip || ''}>
                                                            <div>
                                                                <FeatureTitle 
                                                                    style={{ fontStyle: planFeature.italic ? 'italic' : 'normal' }}
                                                                    dangerouslySetInnerHTML={{ __html: selectedCycle === 'year' && planFeature.valueYearly ? planFeature.valueYearly : planFeature.value }}
                                                                />
                                                            </div>
                                                        </TooltipWrapper>
                                                        {planFeature.comingSoon && (
                                                            <Badge>
                                                                <span>Coming soon</span>
                                                            </Badge>
                                                        )}
                                                        {planFeature.isNew && (
                                                            <Badge>
                                                                <span>NEW</span>
                                                            </Badge>
                                                        )}
                                                    </Feature>
                                                ))}
                                        </FeatureList>
                                    </div>
                                </PlanCard>
                            </div>
                        )
                    }
                    return null; // Return null if no selectedPrice found
                })}
            </PlansRow>
            {/* Enterprise plan - full width below other plans */}
            <EnterpriseContainer>
                <EnterpriseLeftSection>
                    <Text $white $bold $heading2>Enterprise</Text>
                    <EnterpriseDescription>
                        <Text $white $paragraph1>Built for 100-10,000 people organizations that want to standardize relationship selling across their enterprise.</Text>
                    </EnterpriseDescription>
                    <EnterpriseFeaturesGrid>
                        <EnterpriseFeature>
                            <StyledSvg primaryColor><SvgFragment type="checkmark" /></StyledSvg>
                            <div>
                                <Text $white $bold $paragraph1>Deploy at scale</Text>
                                <Text $white $paragraph1>Roll out across teams and regions</Text>
                            </div>
                        </EnterpriseFeature>
                        <EnterpriseFeature>
                            <StyledSvg primaryColor><SvgFragment type="checkmark" /></StyledSvg>
                            <div>
                                <Text $white $bold $paragraph1>Influence the roadmap</Text>
                                <Text $white $paragraph1>Product Advisory Board seat</Text>
                            </div>
                        </EnterpriseFeature>
                        <EnterpriseFeature>
                            <StyledSvg primaryColor><SvgFragment type="checkmark" /></StyledSvg>
                            <div>
                                <Text $white $bold $paragraph1>Get a true partner</Text>
                                <Text $white $paragraph1>Dedicated Customer Success for proactive coaching and QBRs</Text>
                            </div>
                        </EnterpriseFeature>
                        <EnterpriseFeature>
                            <StyledSvg primaryColor><SvgFragment type="checkmark" /></StyledSvg>
                            <div>
                                <Text $white $bold $paragraph1>Custom built security requirements</Text>
                                <Text $white $paragraph1>SSO (SAML & OIDC) for secure, compliant sign-in</Text>
                            </div>
                        </EnterpriseFeature>
                        <EnterpriseFeature>
                            <StyledSvg primaryColor><SvgFragment type="checkmark" /></StyledSvg>
                            <div>
                                <Text $white $bold $paragraph1>Launch smoothly</Text>
                                <Text $white $paragraph1>Custom onboarding & team training tailored to your playbooks</Text>
                            </div>
                        </EnterpriseFeature>
                        <EnterpriseFeature>
                            <StyledSvg primaryColor><SvgFragment type="checkmark" /></StyledSvg>
                            <div>
                                <Text $white $bold $paragraph1>Fit your stack</Text>
                                <Text $white $paragraph1>Custom integrations to connect your CRM, data, and internal tools</Text>
                            </div>
                        </EnterpriseFeature>
                    </EnterpriseFeaturesGrid>
                </EnterpriseLeftSection>
                <EnterpriseRightSection>
                    <EnterpriseWhiteCard>
                        <Row style={{ alignItems: 'center', justifyContent: 'center' }}>
                            <Text $black $bold $heading3>Ready to scale?</Text>
                        </Row>
                        <EnterpriseRightDescription>
                            <Text $black $paragraph1>Enterprise plans are customized: during scoping we'll align on seats, credits, integrations, and security needs.</Text>
                        </EnterpriseRightDescription>
                        <EnterpriseButton>
                            <Button
                                $type='blue'
                                onClickHandler={() => openDemoProduct()}
                            >Contact Sales</Button>
                        </EnterpriseButton>
                        <EnterpriseTestimonial>
                            <Row style={{ alignItems: 'center', justifyContent: 'center' }}>
                                <Text $black $italic $paragraph1>
                                    "LeadDelta workspaces turn conversations into opportunities. We can act quickly and as a team."
                                </Text>
                            </Row>
                            <Row style={{ alignItems: 'center', justifyContent: 'center' }}>
                                <Text $black $paragraph2>
                                    Global Team, HarCourt (USA, France, UK)
                                </Text>
                            </Row>
                        </EnterpriseTestimonial>
                    </EnterpriseWhiteCard>
                </EnterpriseRightSection>
            </EnterpriseContainer>
        </Container>
    )
}

const Container = styled.section<IBillingPlansStyles>`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
`

const PlansRow = styled.div`
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 24px;
`

const EnterpriseContainer = styled.div`
    width: 100%;
    max-width: 1214px;
    display: flex;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
`
const AdvancedColaborationCard = styled.div<IBillingPlansStyles>`
    width: 386px;
    height: 28px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 6px 6px 0 0;
    color: ${styles.colors.black600};
    ${({ $backgroundColor }) => $backgroundColor ? `background-color: ${styles.colors.yellow600};` : null}
    
    /* For Enterprise plan, make it full width */
    ${({ $isEnterprise }) => $isEnterprise ? `
        width: 100%;
    ` : null}
`
const PlanCard = styled.div<IBillingPlansStyles>`
    width: 386px;
    min-height: 686px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
   
    border: 2px solid ${styles.colors.black200};
    ${({ $border }) => $border ? `
        border: 2px solid ${styles.colors.yellow600};
        border-radius: 0 0 8px 8px;
    ` : `
        border-radius: 8px;
    `}

    &:hover {
        box-shadow: 0px 0px 12px 0px rgba(51, 57, 81, 0.10);
        border: 2px solid ${styles.colors.primary600};
    }
    ${({ $disabled }) => $disabled ? `
            pointer-events: 'none';
            opacity: 0.6;
    ` : null
    }

    & > div:first-child {
        display: flex;
        flex-direction: column;
        gap: 16px;
        padding: 32px 24px 24px 24px;
        height: 100%;
    }
 `
const TitleSection = styled.section`
    display: flex;
    justify-content: space-between;
    margin-bottom: 20px;
`
const PriceSection = styled.section`
    display: flex;
    flex-direction: column;
    gap: 4px;
`
const PriceWrapper = styled.div`
    display: flex;
    gap: 4px;
`
const PriceText = styled(Text)`
    font-size: 40px;
    line-height: 40px;
    font-weight: 700;
`;
const FractionalPart = styled.div`
    display: flex;
    flex-direction: column;
    margin: 0 0 0 4px;
    justify-content: flex-end;
`
const IntervalText = styled(Text)`
    height: 22px;
    font-size: 14px;
    line-height: 24px;
    font-weight: 700;
    color: ${styles.colors.black400};
`;
const Feature = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 4px;
`

const FeatureTitle = styled.span`
    font-family: 'Nunito Sans', sans-serif;
    font-weight: 400;
    font-style: normal;
    font-size: 16px;
    line-height: 24px;
    letter-spacing: 0px;
    text-decoration: underline;
    text-decoration-style: dotted;
    text-decoration-color: ${styles.colors.disabledState};
    text-decoration-offset: 16%;
    text-decoration-thickness: 12%;
    text-decoration-skip-ink: auto;
    color: ${styles.colors.black400};
    cursor: help;
`
const FeatureList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`
const Description = styled.div`
    height: 72px;
`
const StyledSvg = styled.div<{primaryColor?: boolean, yellow?: boolean}>`
  .svg{
    width: 20px;
    height: 20px;
    ${({primaryColor, yellow}) => {
      if (yellow) return `& path { fill: ${styles.colors.yellow600} }`;
      if (primaryColor) return `& path { fill: ${styles.colors.primary600} }`;
      return '';
    }}
  }
`
const CreditsBenefitsDiv = styled.div`
  border-radius: 8px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  background-color: ${styles.colors.black100};
  gap: 8px;
`;
const BenefitRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const Badge = styled.div`
    height: 18px;
    border-radius: 4px;
    padding: 0 6px;
    background: #EEF3FF;
    display: flex;
    align-items: center;
    justify-content: center;
    
    span {
        font-size: 12px;
        font-weight: 700;
        color: #5964FF;
    }
`;
const EnterpriseLeftSection = styled.div`
    flex: 1;
    background-color: #282C3F;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;
const EnterpriseDescription = styled.div`
    margin-bottom: 8px;
`;
const EnterpriseFeaturesGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px 8px;
    flex: 1;
`;
const EnterpriseFeature = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    
    div {
        display: flex;
        flex-direction: column;
    }
`;
const EnterpriseRightSection = styled.div`
    width: 518px;
    background-color: #282C3F;
    padding: 24px 0px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
`;
const EnterpriseWhiteCard = styled.div`
    background-color: white;
    border-radius: 12px;
    padding: 24px;
    height: 100%;
    width: 100%;
    max-width: 470px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    gap: 12px;
`;
const EnterpriseRightDescription = styled.div`
    margin-bottom: 12px;
`;
const EnterpriseButton = styled.div`
    margin-bottom: 28px;
`;
const EnterpriseTestimonial = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const EnterpriseFeatureTitle = styled.span`
    font-family: 'Nunito Sans', sans-serif;
    font-weight: 400;
    font-style: normal;
    font-size: 16px;
    line-height: 24px;
    letter-spacing: 0px;
    text-decoration: underline;
    text-decoration-style: dotted;
    text-decoration-offset: 16%;
    text-decoration-thickness: 12%;
    text-decoration-skip-ink: auto;
`;

export default BillingPlans